import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, Icon, cn } from "@nexus/ui";
import { usePermissions } from "@/features/auth/hooks/usePermissions";
import type { PreflightReportDetail } from "../../types/preflight.types";
import { formatReportContext, formatReportTimestamp } from "../../lib/report-meta";
import { ChangeReportMarketDialog } from "../ChangeReportMarketDialog/ChangeReportMarketDialog";
import { MarketIcon } from "../MarketIcon/MarketIcon";
import { PlatformIcon } from "../PlatformIcon/PlatformIcon";
import { ReportBackLink } from "../ReportBackLink/ReportBackLink";
import { ReportPageActions } from "../ReportPageActions/ReportPageActions";
import { ReportSummaryMetrics } from "../ReportSummaryMetrics/ReportSummaryMetrics";

export interface ReportPageHeaderProps {
  report: PreflightReportDetail;
  showExport: boolean;
  onBack: () => void;
  onDeleted?: () => void;
}

/** Report identity, actions, and inline aggregates in one cohesive header block. */
export function ReportPageHeader({
  report,
  showExport,
  onBack,
  onDeleted,
}: ReportPageHeaderProps) {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const [isMarketDialogOpen, setIsMarketDialogOpen] = useState(false);
  const canChangeMarket = permissions.PREFLIGHT_RENAME_REPORT.isOn;

  const reportContext = formatReportContext({
    platform: t(`preflight.platform.${report.platform}`),
    placement: t(`preflight.placement.${report.placement}`),
  });
  /** Reports created before the market backfill carry no market (R6, R27). */
  const market = report.market;
  const marketName = market?.name ?? t("preflight.markets.none", "No market");
  const createdAt = formatReportTimestamp(report.createdAt);
  const byline = report.createdBy
    ? t("preflight.report.generatedByOn", {
        name: report.createdBy.displayName,
        date: createdAt,
      })
    : t("preflight.report.generatedOn", { date: createdAt });

  return (
    <section
      className="flex flex-col gap-3"
      aria-labelledby="preflight-report-title"
    >
      <ReportBackLink onBack={onBack} />

      <Card className="border-border gap-0 overflow-hidden py-0">
        <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1">
            <h1
              id="preflight-report-title"
              className="text-foreground truncate text-xl font-semibold tracking-tight"
            >
              {report.name}
            </h1>
            <p className="text-muted-foreground mt-1 text-sm">
              <span className="inline-flex items-center gap-1.5 align-middle">
                <PlatformIcon platform={report.platform} />
                {reportContext}
              </span>
              <span aria-hidden="true">{" · "}</span>
              {canChangeMarket ? (
                <button
                  type="button"
                  onClick={() => setIsMarketDialogOpen(true)}
                  className={cn(
                    "hover:text-foreground inline-flex cursor-pointer items-center gap-1.5 rounded-sm align-middle transition-colors duration-200",
                    "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
                  )}
                  aria-label={t(
                    "preflight.changeMarket.triggerAria",
                    "Change market: {{market}}",
                    { market: marketName },
                  )}
                >
                  {market ? (
                    <MarketIcon
                      kind={market.kind}
                      code={market.code}
                      name={market.name}
                    />
                  ) : null}
                  {marketName}
                  <Icon
                    name="pencil"
                    size="xs"
                    className="text-muted-foreground"
                    aria-hidden
                  />
                </button>
              ) : (
                <span className="inline-flex items-center gap-1.5 align-middle">
                  {market ? (
                    <MarketIcon
                      kind={market.kind}
                      code={market.code}
                      name={market.name}
                    />
                  ) : null}
                  {marketName}
                </span>
              )}
            </p>
            <p className="text-muted-foreground/80 mt-1 text-xs">{byline}</p>
          </div>
          <ReportPageActions
            report={report}
            showExport={showExport}
            onDeleted={onDeleted}
          />
        </div>

        <ReportSummaryMetrics
          report={report}
          className="border-border bg-muted/70 rounded-none border-t px-5 py-6 sm:px-8 sm:py-7"
        />
      </Card>

      {canChangeMarket && isMarketDialogOpen ? (
        <ChangeReportMarketDialog
          report={report}
          open={isMarketDialogOpen}
          onOpenChange={setIsMarketDialogOpen}
          trigger={null}
        />
      ) : null}
    </section>
  );
}
